import { createStorageApi } from '@d-orca/shared/client/apis';
import { authedFetch, UnauthorizedError } from '@apis/auth';

async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  // 401 reaches us only after authedFetch's refresh-and-retry has already
  // failed and dispatched `dorca:auth-expired`; we just surface the error
  // type so React Query lands in an error state until the redirect fires.
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}

/**
 * Fetches the building view SVG for a site as a UTF-8 string. The backend
 * proxies bytes from Trusstor's storage gateway. Returns null when the
 * upstream has no SVG for this project (404) so callers can fall back to a
 * synthetic visualisation rather than treating it as an error.
 */
export async function fetchBuildingViewSvg(
  siteId: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/storage/building-view`, {
    signal,
  });
  if (res.status === 404) return null;
  if (!res.ok) return handle(res);
  return res.text();
}

// The per-trade pictogram and floor plans: their HTTP shape is shared with the mobile app.
export const storageApi = createStorageApi(authedFetch);
export const { fetchTradeIconSvg, fetchFloorViewSvg } = storageApi;

/**
 * Fetches a named glyph from trusstor's shared icon bucket — e.g. the
 * monitor tab strip's `users`, `Workplan`, `issues`, `shield`. Names
 * are case-sensitive (Trusstor uses mixed case). Returns null on 404
 * so callers can render a blank space instead of erroring.
 */
export async function fetchNamedIconSvg(
  siteId: string,
  name: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/storage/icons/${encodeURIComponent(name)}`,
    { signal },
  );
  if (res.status === 404) return null;
  if (!res.ok) return handle(res);
  return res.text();
}

/**
 * The site's project photo (the portfolio card's photo panel), or null when
 * there is none. Any failure counts as "no photo" — the panel shows its
 * gradient fallback — so this never throws for an HTTP status.
 */
export async function fetchProjectPhoto(siteId: string, signal?: AbortSignal): Promise<Blob | null> {
  const res = await authedFetch(`/api/sites/${encodeURIComponent(siteId)}/storage/project-image`, { signal });
  return res.ok ? res.blob() : null;
}
